import React, { FC, Fragment, useEffect, useState } from 'react';
import { Button, Checkbox, Form, Input, Row, Select } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CustomInputText,
  CustomInputTextArea,
  CustomOnlyNumber,
} from 'src/components/forms/pin/CustomInputText';
import { ComboData, IAddressData, IAddressDataMexico } from 'src/shared/models';
import {
  customRegexName,
  phoneRegex,
  useWindowSize,
  zipCodeFormatMexico,
  directionCharacters,
  streetNumberMX,
} from 'utils';
import classNames from 'classnames';
import { IAddressFormValues } from 'src/components/forms/interfaces';

interface Props {
  address?: IAddressDataMexico;
  isEditForm?: boolean;
  isFirstAddress?: boolean;
  onCreateAddress?: (address: IAddressData) => void;
  onUpdateAddress?: (address: IAddressData) => void;
  suburbList: ComboData[];
}

const AddressFormMx: FC<Props> = ({
  address,
  isEditForm,
  isFirstAddress,
  onCreateAddress,
  onUpdateAddress,
  suburbList,
}): JSX.Element => {
  const [form] = Form.useForm();
  const { isMD } = useWindowSize();
  const { formatMessage } = useIntl();
  const [addressData, setAddressData] = useState(address);
  const zipCodeFiltered = suburbList;
  const [suburbListFiltered, setSuburbsListFiltered] = useState<ComboData[]>(
    [],
  );
  const [suburbDisabled, setSuburbDisabled] = useState(true);

  const formValues: Partial<IAddressDataMexico> = {
    name: address?.name || '',
    surname: address?.surname || '',
    address: address?.address.includes('#')
      ? address.address.substring(0, address.address.indexOf('#'))
      : address?.address || '',
    addressOptionalInfo: address?.address.includes('#')
      ? address?.address.substring(address.address.indexOf('#') + 1)
      : '',
    phone: address?.phone || '',
    zipCode: address?.zipCode || '',
    city: address?.city || '',
    regionName: address?.regionName || '',
    deliveryInstructions: address?.deliveryInstructions || '',
    district: address?.district || null,
    streetNumber: address?.streetNumber || null,
  };

  let formFields: IAddressFormValues[] = [
    {
      type: 'text',
      name: 'name',
      placeholder: 'form.field.name',
      handleChange: 'name',
      initialValue: formValues.name,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.name' }) },
        ),
      },
      autocomplete: 'given-name',
      length: 25,
    },
    {
      type: 'text',
      name: 'surname',
      placeholder: 'form.field.last-name',
      handleChange: 'surname',
      initialValue: formValues.surname,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.last-name' }) },
        ),
      },
      autocomplete: 'family-name',
      length: 50,
    },
    {
      type: 'text',
      name: 'address',
      placeholder: 'form.field.address',
      handleChange: 'address',
      initialValue: formValues.address,
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      autocomplete: 'street-address',
      length: 40,
    },
    {
      type: 'text',
      name: 'streetNumber',
      placeholder: 'form.field.streetNumber',
      handleChange: 'streetNumber',
      initialValue: formValues.streetNumber,
      rules: {
        pattern: streetNumberMX,
        message: formatMessage({ id: 'form.field.error-only-numbers' }),
      },
      autocomplete: 'address-line1',
      length: 5,
    },
    {
      type: 'optionalText',
      name: 'addressOptionalInfo',
      placeholder: 'form.field.addressOptionalInfo',
      handleChange: 'addressOptionalInfo',
      initialValue: formValues.addressOptionalInfo,
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      autocomplete: 'address-line2',
      length: 20,
    },
    {
      type: 'number',
      name: 'phone',
      placeholder: 'form.field.telephone',
      handleChange: 'phone',
      initialValue: formValues.phone,
      rules: {
        pattern: phoneRegex.MX,
        message: formatMessage({
          id: 'form.field.phone-error-number',
        }),
      },
      autocomplete: 'tel',
      length: 10,
    },
  ];

  let formFieldsNext = [
    {
      type: 'optionalTextArea',
      name: 'deliveryInstructions',
      placeholder: 'form.field.deliveryInstructions',
      handleChange: 'deliveryInstructions',
      initialValue: '',
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      autocomplete: null,
    },
  ];

  useEffect(() => {
    checkCanContinue();
  });

  useEffect(() => {
    form.resetFields();
    if (!addressData) return;
    form.setFieldsValue(formValues);
    setAddressData((data) => {
      return {
        ...data,
        address: formValues.address,
        addressOptionalInfo: formValues.addressOptionalInfo,
      };
    });
  }, []);

  useEffect(() => {
    let suburbsListFilter: ComboData[] = [];
    if (address?.zipCode.match(zipCodeFormatMexico)) {
      for (let i = 0; i < suburbList?.length; i++) {
        if (address?.zipCode === suburbList[i].value) {
          suburbsListFilter?.push(suburbList[i]);
        }
      }
    }
    setSuburbsListFiltered(suburbsListFilter);

    if (!isFirstAddress) return;

    setAddressData((currentAddress) => {
      return {
        ...currentAddress,
        defaultAddress: true,
      };
    });
  }, []);

  const isDefaultAddressChecked = (): boolean => {
    if ((!isEditForm && isFirstAddress) || address?.defaultAddress) {
      return true;
    }
    return false;
  };

  const isDefaultAddressDisabled = () => {
    const editingDefaultAddress = Boolean(isEditForm && address.defaultAddress);

    if (isFirstAddress || editingDefaultAddress) return true;
    return false;
  };

  const getCompleteAdressData = () => {
    return addressData.addressOptionalInfo
      ? `${addressData.address}#${addressData.addressOptionalInfo}`
      : addressData.address;
  };

  const handleSubmit = () => {
    let completeAddressData = getCompleteAdressData();
    let newAddress = {
      ...addressData,
      address: completeAddressData,
    };
    isEditForm ? onUpdateAddress(newAddress) : onCreateAddress(newAddress);
    form.resetFields();
  };

  const checkAddressForm = (addressData: IAddressDataMexico): boolean => {
    return Boolean(
      addressData?.name?.match(customRegexName) &&
        addressData?.surname?.match(customRegexName) &&
        addressData?.address &&
        addressData?.phone?.match(phoneRegex.MX) &&
        addressData?.zipCode?.match(zipCodeFormatMexico) &&
        addressData?.city &&
        addressData?.regionName &&
        addressData?.district &&
        addressData?.streetNumber,
    );
  };

  const checkCanContinue = () => {
    const isDataCorrect = checkAddressForm(addressData);
    if (addressData?.canContinue == isDataCorrect) return;
    setAddressData((data) => {
      return {
        ...data,
        canContinue: isDataCorrect,
      };
    });
  };

  const filterSuburbsByZipCode = (event: string) => {
    setSuburbsListFiltered([]);
    let suburbsListFilter: ComboData[] = [];
    if (event?.match(zipCodeFormatMexico)) {
      for (let i = 0; i < suburbList?.length; i++) {
        if (event === suburbList[i].value) {
          suburbsListFilter?.push(suburbList[i]);
        }
      }
    }
    setSuburbsListFiltered(suburbsListFilter);
    let newCity;
    let newState;
    if (event?.match(zipCodeFormatMexico)) {
      newCity = suburbsListFilter[0]?.description.substring(
        suburbsListFilter[0]?.description.indexOf('#') + 1,
        suburbsListFilter[0]?.description.indexOf('@'),
      );
      newState = suburbsListFilter[0]?.description.substring(
        suburbsListFilter[0]?.description.indexOf('@') + 1,
      );
    } else {
      newCity = '';
      newState = '';
    }
    form.setFieldsValue({
      city: newCity ?? undefined,
      regionName: newState ?? undefined,
      district: undefined,
    });
    setSuburbsListFiltered;
    setAddressData((data) => {
      const newData = { ...data };
      newData.city = newCity ?? undefined;
      newData.regionName = newState ?? undefined;
      return newData;
    });
  };

  useEffect(() => {
    setSuburbDisabled(!addressData?.zipCode.match(zipCodeFormatMexico));
    filterSuburbsByZipCode(addressData?.zipCode);
  }, [addressData?.zipCode]);

  const handleChange = (field: string) => (event: any) => {
    if (event.persist) event.persist();
    setAddressData((data) => {
      const newData = { ...data };
      let value = event?.target?.value;
      switch (field) {
        case 'phone':
        case 'zipCode':
          value = event;
          break;
        case 'defaultAddress':
          value = event.target.checked;
          break;
        default:
          value = event.target.value;
          break;
      }
      return { ...newData, [field]: value };
    });
  };

  const renderTitle = (
    <>
      {isMD && (
        <h4 className="address__form-title">
          {isEditForm
            ? formatMessage({ id: 'form.address.edit-title' })
            : formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
      {!isMD && !isEditForm && (
        <h4 className="address__form-title">
          {formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
    </>
  );

  const renderFields = formFields.map((field) => {
    let component: JSX.Element;

    switch (field.type) {
      case 'text':
        component = CustomInputText(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length,
          true,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      case 'optionalText':
        component = CustomInputText(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length,
          false,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      case 'number':
        component = CustomOnlyNumber(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length ?? 40,
          true,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      default:
        return;
    }

    return <Fragment key={field.name}>{component}</Fragment>;
  });

  const renderFieldsNext = formFieldsNext.map((field) => {
    let component: JSX.Element;

    switch (field.type) {
      case 'optionalTextArea':
        component = CustomInputTextArea(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          500,
          false,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      default:
        return;
    }

    return <Fragment key={field.name}>{component}</Fragment>;
  });

  const handleChangeSelect = (value) => {
    if (value.zipCode) {
      setSuburbsListFiltered([]);
      form.setFieldsValue({
        district: null,
      });
      setSuburbsListFiltered;
      setAddressData((data) => {
        const newData = { ...data };
        newData.zipCode = value.zipCode.toString();
        newData.district = undefined;
        return newData;
      });
    }
    if (value.district) {
      setAddressData((data) => {
        const newData = { ...data };
        newData.district = value.district.substring(
          0,
          value.district.indexOf('#'),
        );
        return newData;
      });
    }
  };

  let componentZipCode = CustomOnlyNumber(
    form,
    'zipCode',
    'form.field.zipCode',
    handleChange('zipCode'),
    formValues.zipCode,
    5 ?? 40,
    true,
    [
      ({ getFieldValue }) => ({
        validator(rule, value) {
          const zipCodeCorrect = zipCodeFiltered?.some(
            (e) => e.value === value,
          );
          if (!zipCodeCorrect || !value.match(zipCodeFormatMexico)) {
            setAddressData((data) => {
              const newData = { ...data };
              newData.city = undefined;
              newData.regionName = undefined;
              newData.district = undefined;
              return newData;
            });
            return Promise.reject(
              formatMessage({ id: 'form.field.zipCode-error' }),
            );
          } else {
            return Promise.resolve();
          }
        },
      }),
    ],
    null,
    'postal-code',
  );

  const formItemClassNames: string = classNames(
    'form-con',
    'custom-text-field',
  );

  return (
    <>
      {renderTitle}
      <Form
        name="addressForm"
        form={form}
        className="address__form"
        onFinish={handleSubmit}
        onValuesChange={handleChangeSelect}>
        {renderFields}
        <Fragment key={'zipCode'}>{componentZipCode}</Fragment>
        <Row>
          <Form.Item name="regionName">
            <div className={formItemClassNames}>
              <Input
                defaultValue={
                  form?.getFieldValue('regionName') || formValues?.regionName
                }
                value={addressData?.regionName}
                type={'text'}
                disabled={true}
                placeholder=" "
              />
              <label>
                <FormattedMessage id={'form.field.state'} />
              </label>
            </div>
          </Form.Item>
        </Row>
        <Row>
          <Form.Item name="city">
            <div className={formItemClassNames}>
              <Input
                defaultValue={form?.getFieldValue('city') || formValues?.city}
                value={addressData?.city}
                type={'text'}
                disabled={true}
                placeholder=" "
              />
              <label>
                <FormattedMessage id={'form.field.city'} />
              </label>
            </div>
          </Form.Item>
        </Row>
        <Row>
          <div className="custom-text-field">
            <label>
              <FormattedMessage id={'form.field.suburb'} />
            </label>
          </div>
          <Form.Item name="district">
            <Select
              defaultValue={formValues.district}
              disabled={
                suburbDisabled ||
                !suburbList?.some((e) => e.value === addressData?.zipCode)
              }
              className="custom-text-field customMarginBottomLast"
              placeholder=" ">
              {suburbListFiltered?.map((suburb, index) => (
                <Select.Option key={index} value={suburb.description}>
                  {suburb.description.substring(
                    0,
                    suburb.description.indexOf('#'),
                  )}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Row>
        {renderFieldsNext}
        {!isDefaultAddressDisabled() && (
          <Form.Item
            name="defaultAddress"
            valuePropName="checked"
            initialValue={isDefaultAddressChecked()}>
            <Checkbox
              className="address__form__button-wrapper"
              defaultChecked={isDefaultAddressChecked()}
              onChange={handleChange('defaultAddress')}
              disabled={isDefaultAddressDisabled()}>
              {formatMessage({ id: 'form.field.address.preferred' })}
            </Checkbox>
          </Form.Item>
        )}
        <Form.Item shouldUpdate className="address__form__button-wrapper">
          {() => (
            <Button
              htmlType="submit"
              className="address__form__button-submit btn-primary btn-primary--black"
              disabled={!addressData?.canContinue}>
              {formatMessage({ id: 'form.address.add-new' })}
              {isMD && (
                <i className="icon icon--arrow-normal-right-white button-icon" />
              )}
            </Button>
          )}
        </Form.Item>
      </Form>
    </>
  );
};

export default AddressFormMx;
